import { getChartSettingsFromSession } from 'home/AssetHierarchy/actions/assetDetails.actions';
import {
  ANNOTATIONS_REQUEST,
  ANNOTATIONS_SUCCESS,
  ANNOTATIONS_FAILURE,
  CLEAR_ANNOTATIONS,
  SET_ANNOTATIONS_VISIBLE,
  TOGGLE_ANNOTATION_TYPE,
  SET_ANNOTATIONS_FILTER,
  SET_ANNOTATION_CREATE_MODE,
  SET_ACTIVE_ANNOTATION_ENTITY
} from '../actions/annotations.types';
import { ANNOTATION_TYPES } from '../utils/annotationTypes';

const chartSettingsForInit = getChartSettingsFromSession();
const initialAnnotationsVisible = !!chartSettingsForInit.showAnnotations;
const initialVisibleTypes = (function computeInitialVisibleTypes() {
  const stored = chartSettingsForInit && chartSettingsForInit.visibleAnnotationTypes;
  if (Array.isArray(stored) && stored.length > 0) return stored;
  return [...ANNOTATION_TYPES];
}());

// Entity key for the per-entity data partition. Each annotated chart owns its own
// normalized slice keyed by `annotatable_type:annotatable_id`, so two charts for
// different tags never clobber each other's data (the fetches land in separate
// buckets and the out-of-order guard is scoped per entity).
export const entityKeyFor = (type, id) => (
  (type != null && id != null && id !== '') ? `${type}:${id}` : null
);
const entityKeyFromParams = params => (
  params ? entityKeyFor(params.annotatable_type, params.annotatable_id) : null
);

// Fresh per-entity bucket. Normalized data plus the fetch bookkeeping that used
// to live at the top level (so the out-of-order guard and fetch-dedup cache are
// scoped to a single entity).
const emptyEntity = () => ({
  byId: {},
  allIds: [],
  loading: false,
  error: null,
  currentRequestId: 0,
  lastFetchedRange: null
});

const initialState = {
  // Per-entity normalized data, keyed by `annotatable_type:annotatable_id`.
  byEntity: {},

  // UI state (global to the page)
  visible: initialAnnotationsVisible, // Initialize from session preference
  visibleTypes: initialVisibleTypes, // Initialize visible types from session
  createMode: false, // Add-note mode: capture click/drag gestures on the chart
  // Which chart (entity key) the pointer is currently over. Create-mode arms
  // only this chart, so 'Add note' doesn't capture on every trend chart at once.
  activeEntity: null,

  // Filters
  filters: {
    severity: [], // ['high', 'critical']
    dateRange: null,
    search: ''
  }
};

// Immutably replace one entity bucket, leaving the rest of byEntity untouched.
const withEntity = (state, key, entity) => ({
  ...state,
  byEntity: { ...state.byEntity, [key]: entity }
});

export default function annotationsReducer(state = initialState, action) {
  switch (action.type) {
    // ===== LOADING ANNOTATIONS =====
    case ANNOTATIONS_REQUEST: {
      const key = entityKeyFromParams(action.meta && action.meta.params);
      if (!key) return state;
      const entity = state.byEntity[key] || emptyEntity();
      return withEntity(state, key, {
        ...entity,
        loading: true,
        error: null,
        currentRequestId: (action.meta && action.meta.requestId) || entity.currentRequestId,
        lastFetchedRange: action.meta && action.meta.params
      });
    }

    case ANNOTATIONS_SUCCESS: {
      const key = entityKeyFromParams(action.meta && action.meta.params);
      if (!key) return state;
      const entity = state.byEntity[key] || emptyEntity();
      // Ignore responses superseded by a newer in-flight request FOR THIS ENTITY.
      const successRequestId = action.meta && action.meta.requestId;
      if (successRequestId && successRequestId !== entity.currentRequestId) {
        return state;
      }
      const annotations = action.payload.annotations || [];
      // Replace this entity's annotations with the latest payload to avoid
      // showing stale annotations when the API returns an empty set.
      const byId = {};
      const allIds = [];
      annotations.forEach((annotation) => {
        if (!annotation || !annotation.id) return;
        byId[annotation.id] = annotation;
        allIds.push(annotation.id);
      });
      return withEntity(state, key, {
        ...entity,
        loading: false,
        error: null,
        byId,
        allIds
      });
    }

    case ANNOTATIONS_FAILURE: {
      const key = entityKeyFromParams(action.meta && action.meta.params);
      if (!key) return state;
      const entity = state.byEntity[key] || emptyEntity();
      // Ignore failures from superseded requests for this entity.
      const failureRequestId = action.meta && action.meta.requestId;
      if (failureRequestId && failureRequestId !== entity.currentRequestId) {
        return state;
      }
      return withEntity(state, key, {
        ...entity,
        loading: false,
        error: action.error
      });
    }

    // ===== UI STATE MANAGEMENT =====
    case CLEAR_ANNOTATIONS:
      return {
        ...state,
        byEntity: {},
        // Add-note mode and the focused chart are transient UI state; don't let
        // them survive a reset.
        createMode: false,
        activeEntity: null
      };

    case SET_ANNOTATIONS_VISIBLE:
      return {
        ...state,
        visible: action.payload.visible,
        // Hiding annotations must also disarm add-note mode. It used to be
        // cleared only by AnnotationControls unmounting, which made the reset
        // depend on that component still being rendered -- so any view that
        // hides annotations while keeping the controls mounted left create-mode
        // silently armed, capturing the next click on a chart.
        createMode: action.payload.visible ? state.createMode : false,
        activeEntity: action.payload.visible ? state.activeEntity : null
      };

    case SET_ANNOTATION_CREATE_MODE:
      return {
        ...state,
        createMode: action.payload.active,
        // Leaving create-mode drops the focused chart so a stale focus can't
        // re-arm a chart the next time create-mode turns on.
        activeEntity: action.payload.active ? state.activeEntity : null
      };

    case SET_ACTIVE_ANNOTATION_ENTITY:
      return {
        ...state,
        activeEntity: action.payload.entityKey
      };

    case TOGGLE_ANNOTATION_TYPE: {
      const { annotationType } = action.payload;
      const visibleTypes = [...state.visibleTypes];

      if (visibleTypes.includes(annotationType)) {
        // Remove type from visible list
        const index = visibleTypes.indexOf(annotationType);
        visibleTypes.splice(index, 1);
      } else {
        // Add type to visible list
        visibleTypes.push(annotationType);
      }

      return {
        ...state,
        visibleTypes
      };
    }

    case SET_ANNOTATIONS_FILTER: {
      const { filterType, value } = action.payload;
      return {
        ...state,
        filters: {
          ...state.filters,
          [filterType]: value
        }
      };
    }

    default:
      return state;
  }
}
